import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { FileSpreadsheet, FileText, Download } from "lucide-react";
import Spinner from "../../components/Shared/Spinner";
import WordViewerModal from "../../components/Dashboard/IFRSExams/WordViewerModal";
import { ModalProvider } from "../../contexts/ModalContext";
import { toRows } from "../../lib/spreadsheetRows";
import {
  GetIFRSAttemptDetail,
  IFRSDetailQuestion,
  IFRSResponseOption,
  IFRSResponseOptionAnswer,
} from "../../api/Quiz/GetIFRSAttemptDetail";
import { GradeIFRSAttempt } from "../../api/Quiz/GradeIFRSAttempt";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";
  return `${day}.${month}.${date.getFullYear()} · ${hours}:${minutes} ${ampm}`;
};

const SLOT_LABELS: Record<string, string> = {
  wordProcessor: "Written answer",
  spreadsheet: "Spreadsheet",
};

const actionClass =
  "shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-[#4379EE] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#3568d4] focus:outline-none focus:ring-2 focus:ring-[#4379EE]/40";

/**
 * Spreadsheets answered inside the exam are stored as cells, not as a file, so
 * there is nothing to link to until one is built. Rebuild the workbook on
 * demand — a marker opens these in Excel, not in a web page.
 */
const downloadSpreadsheet = (data: unknown, filename: string) => {
  const sheet = XLSX.utils.aoa_to_sheet(toRows(data as never));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Answer");
  XLSX.writeFile(book, filename);
};

/** Strips markup so a preview line can't drag the document's own layout in. */
const plainText = (html?: string | null) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * One submitted answer: a thing to open, not a thing to read in place.
 * Spreadsheets belong in Excel, long prose belongs at reading width.
 */
const AnswerRow = ({
  answer,
  option,
  questionLabel,
}: {
  answer: IFRSResponseOptionAnswer;
  option: IFRSResponseOption;
  questionLabel: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Uploaded answers live on S3; the field is named inconsistently across the
  // two endpoints that return them.
  const fileUrl = answer.answerFilePath || answer.answerFileUrl;
  const isWritten =
    !fileUrl &&
    (answer.answerData?.type === "wordProcessor" ||
      option.fileType === "wordProcessor");

  let detail = "Saved in the exam";
  let action: JSX.Element | null = null;

  if (fileUrl) {
    detail =
      decodeURIComponent(fileUrl.split("/").pop() || "").replace(/^\d+_/, "") ||
      "Uploaded file";
    action = (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        className={actionClass}
      >
        <Download size={14} /> Download
      </a>
    );
  } else if (isWritten) {
    const words = plainText(answer.answerData?.data as string)
      .split(/\s+/)
      .filter(Boolean).length;
    detail = `${words} word${words === 1 ? "" : "s"} typed`;
    action = (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={actionClass}
      >
        Read answer
      </button>
    );
  } else if (answer.answerData?.type === "spreadsheet") {
    const rows = toRows(answer.answerData.data as never);
    detail = `${rows.length} row${rows.length === 1 ? "" : "s"} of workings`;
    action = (
      <button
        type="button"
        onClick={() =>
          downloadSpreadsheet(
            answer.answerData!.data,
            `${questionLabel.toLowerCase().replace(/\s+/g, "-")}.xlsx`
          )
        }
        className={actionClass}
      >
        <Download size={14} /> Download .xlsx
      </button>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 rounded-xl border border-black/5 bg-[#F7F9FC] px-3 py-2.5">
        {/* Prose and workings are different jobs to a marker, so they are
            different colours — the only place this page uses a second hue. */}
        <span
          className={`grid size-9 shrink-0 place-items-center rounded-lg ${
            isWritten
              ? "bg-[#4379EE]/10 text-[#4379EE]"
              : "bg-[#12A08C]/10 text-[#12A08C]"
          }`}
        >
          {isWritten ? <FileText size={17} /> : <FileSpreadsheet size={17} />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-burntBlack">
            {SLOT_LABELS[option.fileType] || option.fileType}
          </p>
          <p className="truncate text-xs text-lightGray">{detail}</p>
        </div>
        <div className="ml-auto">{action}</div>
      </div>
      {isWritten && (
        <WordViewerModal
          html={String(answer.answerData?.data || "")}
          eyebrow="Written answer"
          label={questionLabel}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

/**
 * One question of the script. Marking happens on the same line as the question
 * number, so the marker never has to map a mark box back to what it belongs to.
 */
const QuestionRow = ({
  question,
  index,
  attemptId,
  mark,
  onMarkChange,
}: {
  question: IFRSDetailQuestion;
  index: number;
  attemptId: string;
  mark: string;
  onMarkChange: (value: string) => void;
}) => {
  const [questionOpen, setQuestionOpen] = useState(false);
  const label = `Question ${index + 1}`;

  const questionHtml = [question.description, question.requirements]
    .filter(Boolean)
    .join("");
  const preview = plainText(questionHtml);

  // The API returns every attempt's answers on each slot, so keep only the
  // ones belonging to the attempt being reviewed.
  const answered = question.responseOptions.flatMap((option) =>
    option.ifrsResponseOptionAnswers
      .filter((a) => a.studentToIfrsId === attemptId)
      .map((answer) => ({ option, answer }))
  );
  // Empty slots are the exam's shape, not the student's work. One quiet line
  // records them; six dashed boxes would bury the answer that does exist.
  const empty = question.responseOptions.length - answered.length;

  return (
    <section className="border-t border-black/10 px-6 py-6 first:border-t-0 sm:px-8">
      <div className="flex items-center gap-4">
        <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-burntBlack">
          Q{index + 1}
        </h2>
        {questionHtml && (
          <button
            type="button"
            onClick={() => setQuestionOpen(true)}
            className="text-xs font-semibold text-[#4379EE] hover:underline focus:outline-none focus:ring-2 focus:ring-[#4379EE]/40"
          >
            Read question
          </button>
        )}
        <span className="h-px flex-1 bg-black/10" />
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            max={question.marks ?? undefined}
            value={mark}
            onChange={(e) => onMarkChange(e.target.value)}
            placeholder="–"
            aria-label={`Marks for question ${index + 1}`}
            className="w-16 rounded-lg border border-black/15 bg-white px-2 py-1.5 text-right text-sm font-semibold tabular-nums text-burntBlack placeholder:font-normal placeholder:text-black/25 focus:border-[#4379EE] focus:outline-none focus:ring-2 focus:ring-[#4379EE]/20"
          />
          <span className="w-14 text-xs text-lightGray tabular-nums">
            / {question.marks ?? "—"}
          </span>
        </div>
      </div>

      {/* One line of the question for orientation; the rest is a click away,
          because an IFRS question can run for pages. */}
      {preview && (
        <p className="mt-2 truncate text-sm text-lightGray">{preview}</p>
      )}

      {answered.length > 0 && (
        <div className="mt-4 space-y-2">
          {answered.map(({ option, answer }) => (
            <AnswerRow
              key={answer.id}
              answer={answer}
              option={option}
              questionLabel={label}
            />
          ))}
        </div>
      )}

      {answered.length === 0 && (
        <p className="mt-3 text-xs italic text-black/35">
          {question.responseOptions.length === 0
            ? "No answer slots on this question"
            : "Nothing submitted"}
        </p>
      )}

      {answered.length > 0 && empty > 0 && (
        <p className="mt-2 text-xs italic text-black/35">
          {empty} further slot{empty === 1 ? "" : "s"} left blank
        </p>
      )}

      <WordViewerModal
        html={questionHtml}
        eyebrow="Question as set"
        label={label}
        isOpen={questionOpen}
        onClose={() => setQuestionOpen(false)}
      />
    </section>
  );
};

/**
 * Marks already awarded on a question, used to seed the inputs. The API returns
 * every student's marks on the question, so match on the attempt.
 */
const existingMark = (question: IFRSDetailQuestion, attemptId: string) => {
  const records = Array.isArray(question.IfrsMarks) ? question.IfrsMarks : [];
  const record = records.find(
    (m) => !m?.studentToIfrsId || m.studentToIfrsId === attemptId
  );
  const value = record?.marks ?? record?.mark;
  return typeof value === "number" ? String(value) : "";
};

const IFRSAttemptAnswers = () => {
  const { courseId, exam_id, attempt_id } = useParams<{
    courseId: string;
    exam_id: string;
    attempt_id: string;
  }>();
  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryFn: () => GetIFRSAttemptDetail(exam_id || "", attempt_id || ""),
    queryKey: ["ifrs-attempt-detail", exam_id, attempt_id],
    enabled: !!exam_id && !!attempt_id,
    refetchOnWindowFocus: false,
  });

  const detail = response?.data;
  const error = response?.error;
  const attempt = detail?.studentExamAttempt;
  const exam = detail?.ifrsExam;
  const student = attempt?.student;
  const questions = exam?.ifrsQuestions ?? [];

  const [marks, setMarks] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the marks from whatever has already been awarded, once the attempt
  // arrives. Done during render so the inputs are never briefly blank.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (detail && seededFor !== attempt_id) {
    setSeededFor(attempt_id || null);
    setMarks(
      Object.fromEntries(
        questions.map((q) => [q.id, existingMark(q, attempt_id || "")])
      )
    );
  }

  const { mutate: saveMarks, isPending } = useMutation({
    mutationFn: () =>
      GradeIFRSAttempt(
        attempt_id || "",
        questions
          .filter((q) => (marks[q.id] ?? "").trim() !== "")
          .map((q) => ({ ifrsQuestionId: q.id, marks: Number(marks[q.id]) }))
      ),
    onSuccess: (result) => {
      if (result.error) {
        setSaveError(result.error.message || "Could not save marks");
        return;
      }
      setSaveError(null);
      setSaved(true);
      queryClient.invalidateQueries({
        queryKey: ["ifrs-attempt-detail", exam_id, attempt_id],
      });
    },
  });

  const awarded = questions.reduce((sum, q) => {
    const value = Number(marks[q.id]);
    return Number.isFinite(value) && marks[q.id] !== "" ? sum + value : sum;
  }, 0);
  const available = questions.reduce((sum, q) => sum + (q.marks ?? 0), 0);
  const marked = questions.filter((q) => (marks[q.id] ?? "") !== "").length;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const attemptsPath = `/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/students`;

  if (error || !detail) {
    return (
      <div className="py-7">
        <Link
          to={attemptsPath}
          className="text-sm font-semibold text-primary hover:text-primary/80"
        >
          ← Back to attempts
        </Link>
        <div className="mt-6 rounded-xl bg-white p-8 text-center text-sm text-red-600 shadow">
          {error?.message || "Failed to load this attempt"}
        </div>
      </div>
    );
  }

  return (
    // The word viewer reuses the exam pages' draggable modal, which tracks
    // stacking through this context. The dashboard has no provider of its own.
    <ModalProvider>
      <div className="pb-10">
      {/* The marking bar: who, how far along, and the one action. It follows
          the marker down the script so the total is never out of sight. */}
      <div className="sticky top-0 z-20 -mx-4 bg-[#F1F4F9]/85 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-xl bg-white px-5 py-4 shadow">
          <div className="min-w-0">
            <Link
              to={attemptsPath}
              className="text-xs font-semibold text-primary hover:text-primary/80"
            >
              ← Back to attempts
            </Link>
            <h1 className="truncate text-xl font-bold leading-tight text-burntBlack">
              {student ? `${student.firstName} ${student.lastName}` : "Attempt"}
            </h1>
            <p className="truncate text-xs text-lightGray">
              {exam?.title}
              {attempt?.submittedAt
                ? ` · submitted ${formatDate(attempt.submittedAt)}`
                : " · never submitted"}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-5">
            {exam?.modelAnswerUrl && (
              <a
                href={exam.modelAnswerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-[#4379EE] underline"
              >
                Model answer ↗
              </a>
            )}
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-[0.12em] text-lightGray">
                {marked} of {questions.length} marked
              </p>
              <p className="text-2xl font-bold leading-tight text-burntBlack tabular-nums">
                {awarded}
                <span className="text-sm font-semibold text-lightGray">
                  {" "}
                  / {available}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                saveMarks();
              }}
              disabled={isPending || questions.length === 0}
              className="rounded-lg bg-[#4379EE] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3568d4] focus:outline-none focus:ring-2 focus:ring-[#4379EE]/40 disabled:bg-black/20"
            >
              {isPending ? "Saving..." : "Save marks"}
            </button>
          </div>

          {(saveError || saved) && (
            <p
              className={`w-full text-xs ${
                saveError ? "text-red-600" : "text-[#4379EE]"
              }`}
            >
              {saveError || "Marks saved."}
            </p>
          )}
        </div>
      </div>

      {/* One continuous script rather than a stack of identical cards. */}
      <div className="mt-4 overflow-hidden rounded-xl bg-white shadow">
        {questions.length === 0 ? (
          <p className="p-10 text-center text-sm text-lightGray">
            This exam has no questions.
          </p>
        ) : (
          questions.map((question, index) => (
            <QuestionRow
              key={question.id}
              question={question}
              index={index}
              attemptId={attempt_id || ""}
              mark={marks[question.id] ?? ""}
              onMarkChange={(value) => {
                setSaved(false);
                setMarks((prev) => ({ ...prev, [question.id]: value }));
              }}
            />
          ))
        )}
        </div>
      </div>
    </ModalProvider>
  );
};

export default IFRSAttemptAnswers;
